<template>
  <div class="">
    <div class="mt-10 sm:mt-0">
      <div class="md:grid md:grid-cols-3 md:gap-6">
        <div class="md:col-span-1">
          <div class="px-4 sm:px-0">
            <h3 class="text-lg font-medium leading-6 text-gray-900">
              {{ $t('practice_info_title') }}
            </h3>
            <p class="mt-1 text-sm leading-5 text-gray-600">
              {{ $t('practice_info_description') }}
            </p>
          </div>
        </div>
        <div class="mt-5 md:mt-0 md:col-span-2">
          <div class="shadow overflow-hidden sm:rounded-md">
            <div class="px-4 py-5 bg-white sm:p-6">
              <div class="grid grid-cols-6 gap-6">
                <div class="col-span-6">
                  <sqr-input-text
                    :label="$t('practice_company')"
                    :value="practice.company"
                    @change="change('company', $event)"
                    :v="$v.company"
                  />
                  <div class="mt-1 text-xs text-gray-600">
                    {{ $t('practice_company_help') }}
                  </div>
                </div>

                <div class="col-span-6 sm:col-span-3">
                  <sqr-input-text
                    :label="$t('practice_given_name')"
                    :value="practice.givenName"
                    @change="change('givenName', $event)"
                    :v="$v.givenName"
                  />
                </div>

                <div class="col-span-6 sm:col-span-3">
                  <sqr-input-text
                    :label="$t('practice_family_name')"
                    :value="practice.familyName"
                    @change="change('familyName', $event)"
                    :v="$v.familyName"
                  />
                </div>

                <div class="col-span-6 sm:col-span-3">
                  <sqr-input-text
                    :label="$t('practice_rcc_number')"
                    :value="practice.rccNumber"
                    @change="change('rccNumber', $event)"
                    :v="$v.rccNumber"
                  />
                  <div
                    class="mt-1 text-xs text-red-600"
                    v-if="$v.rccNumber.$error && !$v.rccNumber.rcc"
                  >
                    {{ $t('validation_rcc') }}
                  </div>
                  <div class="mt-1 text-xs text-gray-600">
                    {{ $t('practice_rcc_number_help_1') }}
                  </div>
                  <div class="mt-1 text-xs text-gray-600">
                    {{ $t('practice_rcc_number_help_2') }}
                  </div>
                </div>

                <div class="col-span-6 sm:col-span-3">
                  <sqr-input-text
                    class="mb-3"
                    :label="$t('practice_gln_number')"
                    :value="practice.glnNumber"
                    @change="change('glnNumber', $event)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="hidden sm:block">
      <div class="py-5">
        <div class="border-t border-gray-200"></div>
      </div>
    </div>

    <div class="mt-10 sm:mt-0">
      <div class="md:grid md:grid-cols-3 md:gap-6">
        <div class="md:col-span-1">
          <div class="px-4 sm:px-0">
            <h3 class="text-lg font-medium leading-6 text-gray-900">
              {{ $t('practice_coordinates_title') }}
            </h3>
            <p class="mt-1 text-sm leading-5 text-gray-600">
              {{ $t('practice_coordinates_description') }}
            </p>
          </div>
        </div>
        <div class="mt-5 md:mt-0 md:col-span-2">
          <div class="shadow overflow-hidden sm:rounded-md">
            <div class="px-4 py-5 bg-white sm:p-6">
              <div class="grid grid-cols-6 gap-6">
                <sqr-input-text
                  class="col-span-6"
                  :label="$t('address_street')"
                  :value="practice.addressStreet"
                  @change="change('addressStreet', $event)"
                  :v="$v.addressStreet"
                />

                <sqr-input-text
                  class="col-span-6 sm:col-span-3 lg:col-span-2"
                  :label="$t('address_postal_code')"
                  :value="practice.postalCode"
                  @change="change('postalCode', $event)"
                  :v="$v.postalCode"
                />

                <div class="col-span-6 sm:col-span-6 lg:col-span-4">
                  <sqr-input-text
                    :label="$t('address_locality')"
                    :value="practice.addressLocality"
                    @change="change('addressLocality', $event)"
                    :v="$v.addressLocality"
                  />
                </div>

                <div class="col-span-6 sm:col-span-3">
                  <sqr-input-email
                    :label="$t('practice_email')"
                    :value="practice.email"
                    @change="change('email', $event)"
                    :v="$v.email"
                  />
                </div>

                <div class="col-span-6 sm:col-span-3">
                  <sqr-input-text
                    :label="$t('practice_telephone')"
                    :value="practice.telephone"
                    @change="change('telephone', $event)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="hidden sm:block">
      <div class="py-5">
        <div class="border-t border-gray-200"></div>
      </div>
    </div>

    <div class="mt-10 sm:mt-0">
      <div class="md:grid md:grid-cols-3 md:gap-6">
        <div class="md:col-span-1">
          <div class="px-4 sm:px-0">
            <h3 class="text-lg font-medium leading-6 text-gray-900">
              {{ $t('practice_payment_title') }}
            </h3>
            <p class="mt-1 text-sm leading-5 text-gray-600">
              {{ $t('practice_payment_description') }}
            </p>
          </div>
        </div>
        <div class="mt-5 md:mt-0 md:col-span-2">
          <div class="shadow overflow-hidden sm:rounded-md">
            <div class="px-4 py-5 bg-white sm:p-6">
              <div class="grid grid-cols-6 gap-6">
                <div class="col-span-6 sm:col-span-3">
                  <sqr-input-text
                    :label="$t('practice_iban')"
                    :value="practice.iban"
                    @change="change('iban', $event)"
                    :v="$v.iban"
                  />
                  <div
                    class="mt-1 text-xs text-red-600"
                    v-if="$v.iban.$error && !$v.iban.iban"
                  >
                    {{ $t('validation_iban') }}
                  </div>
                </div>

                <div class="col-span-6 sm:col-span-3">
                  <sqr-input-text
                    :label="$t('practice_postal_account')"
                    :value="practice.postalAccount"
                    @change="change('postalAccount', $event)"
                    :v="$v.postalAccount"
                  />
                  <div
                    class="mt-1 text-xs text-red-600"
                    v-if="
                      $v.postalAccount.$error && !$v.paymentDelay.postalAccount
                    "
                  >
                    {{ $t('validation_postal_account') }}
                  </div>
                </div>

                <div class="col-span-6 sm:col-span-3">
                  <sqr-input-text
                    :label="$t('practice_vat_number')"
                    :value="practice.vatNumber"
                    @change="change('vatNumber', $event)"
                  />
                  <div class="mt-1 text-xs text-gray-600">
                    {{ $t('practice_vat_number_help') }}
                  </div>
                </div>

                <div class="col-span-6 sm:col-span-3">
                  <sqr-input-number
                    :label="$t('practice_payment_delay')"
                    :value="practice.paymentDelay"
                    @change="change('paymentDelay', $event)"
                    :v="$v.paymentDelay"
                  />
                  <div
                    class="mt-1 text-xs text-red-600"
                    v-if="$v.paymentDelay.$error && !$v.paymentDelay.required"
                  >
                    {{ $t('validation_required') }}
                  </div>
                  <div
                    class="mt-1 text-xs text-red-600"
                    v-if="$v.paymentDelay.$error && !$v.paymentDelay.integer"
                  >
                    {{ $t('validation_integer') }}
                  </div>
                  <div
                    class="mt-1 text-xs text-red-600"
                    v-if="$v.paymentDelay.$error && !$v.paymentDelay.minValue"
                  >
                    {{ $t('validation_min_value') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// tarif5mn, companyNumber, role, responsable de la tva, account number,
// participant number, GLN (b), GLN (p)

import SqrInputText from '@/components/SqrInputText';
import SqrInputEmail from '@/components/SqrInputEmail';
import SqrInputNumber from '@/components/SqrInputNumber';

export default {
  name: 'PracticeForm',
  components: { SqrInputText, SqrInputEmail, SqrInputNumber },
  props: {
    practice: { type: Object, default: () => ({}) },
    validations: { type: Object }
  },
  computed: {
    $v() {
      return this.validations;
    }
  },
  methods: {
    change(field, value) {
      this.$emit('change', { field, value });
    },
    address({ field, value }) {
      this.$emit('change', { field, value });
    }
  }
};
</script>
